import React, { useState } from 'react';
import { reviewApi } from '../../api';
import { toImageUrl } from '../../utils/image';

interface Props {
  productId: number;
  orderItemId: number;
  productName: string;
  thumbnailUrl: string;
  onClose: () => void;
  onSuccess: () => void;
}

interface UploadedImage {
  url: string;
  displayOrder: number;
}

const POINT_REWARD_USD = 0.5;

const ReviewWriteModal: React.FC<Props> = ({ productId, orderItemId, productName, thumbnailUrl, onClose, onSuccess }) => {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [content, setContent] = useState('');
  const [images, setImages] = useState<UploadedImage[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      alert('Image must be 5MB or smaller.');
      return;
    }
    setUploading(true);
    try {
      const res = await reviewApi.uploadImage(file);
      setImages(prev => [...prev, { url: res.data.data, displayOrder: prev.length }]);
    } catch (err: any) {
      alert(err.response?.data?.message || 'Image upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx: number) => {
    setImages(prev => prev.filter((_, i) => i !== idx).map((img, i) => ({ ...img, displayOrder: i })));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (content.trim().length < 10) {
      alert('Review must be at least 10 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await reviewApi.create({ productId, orderItemId, rating, content: content.trim(), images });
      const earned = images.length > 0;
      alert(earned
        ? `Review submitted! You earned $${POINT_REWARD_USD.toFixed(2)} in points.`
        : 'Review submitted!');
      onSuccess();
      onClose();
    } catch (err: any) {
      alert(err.response?.data?.message || 'Failed to submit review.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.backdrop} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <h3 style={styles.title}>Write a Review</h3>
          <button type="button" style={styles.closeBtn} onClick={onClose}>×</button>
        </div>

        <div style={styles.productRow}>
          <img src={thumbnailUrl} alt={productName} style={styles.thumb} />
          <p style={styles.productName}>{productName}</p>
        </div>

        <div style={styles.rewardHint}>
          📷 Add a photo to your review and earn <strong>${POINT_REWARD_USD.toFixed(2)}</strong> in points.
        </div>

        <form onSubmit={handleSubmit}>
          <div style={styles.field}>
            <label style={styles.label}>Rating</label>
            <div style={styles.starInput}>
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHover(n)}
                  onMouseLeave={() => setHover(0)}
                  style={{ ...styles.starBtn, color: n <= (hover || rating) ? '#f5b400' : '#ddd' }}
                >★</button>
              ))}
              <span style={styles.starValue}>{rating}/5</span>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Review</label>
            <textarea
              style={styles.textarea}
              placeholder="Share your experience with this product..."
              value={content}
              onChange={e => setContent(e.target.value)}
              rows={6}
              maxLength={1000}
            />
            <div style={styles.charCount}>{content.length} / 1000</div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Photos (optional)</label>
            <div style={styles.imageGrid}>
              {images.map((img, idx) => (
                <div key={idx} style={styles.imageWrap}>
                  <img src={toImageUrl(img.url)} alt="" style={styles.imageThumb} />
                  <button type="button" style={styles.removeImgBtn} onClick={() => removeImage(idx)}>×</button>
                </div>
              ))}
              {images.length < 5 && (
                <label style={styles.addImgBtn}>
                  {uploading ? '...' : '+'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    disabled={uploading}
                    style={{ display: 'none' }}
                  />
                </label>
              )}
            </div>
            <div style={styles.imageCount}>{images.length} / 5 photos</div>
          </div>

          <div style={styles.actions}>
            <button type="button" style={styles.cancelBtn} onClick={onClose}>Cancel</button>
            <button type="submit" style={styles.submitBtn} disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  backdrop: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', borderRadius: 12, padding: 28, width: 520, maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 },
  title: { fontSize: 18, fontWeight: 700, color: '#333' },
  closeBtn: { background: 'none', border: 'none', fontSize: 28, color: '#999', cursor: 'pointer', lineHeight: 1, padding: 0 },
  productRow: { display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: '#fafafa', borderRadius: 8, marginBottom: 16 },
  thumb: { width: 48, height: 48, objectFit: 'cover', borderRadius: 6, background: '#f0f0f0' },
  productName: { fontSize: 13, fontWeight: 500, color: '#333', lineHeight: 1.4 },
  rewardHint: { background: '#f8f5fd', border: '1px solid #f5d6d6', color: '#a06060', borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 20 },
  field: { marginBottom: 20 },
  label: { display: 'block', fontSize: 13, color: '#555', fontWeight: 600, marginBottom: 8 },
  starInput: { display: 'flex', alignItems: 'center', gap: 4 },
  starBtn: { background: 'none', border: 'none', fontSize: 32, cursor: 'pointer', padding: 0, lineHeight: 1 },
  starValue: { marginLeft: 12, fontSize: 13, color: '#888' },
  textarea: { width: '100%', padding: '12px 14px', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, fontFamily: 'inherit', resize: 'none', outline: 'none', boxSizing: 'border-box' },
  charCount: { fontSize: 11, color: '#bbb', textAlign: 'right', marginTop: 4 },
  imageGrid: { display: 'flex', flexWrap: 'wrap', gap: 8 },
  imageWrap: { position: 'relative', width: 72, height: 72 },
  imageThumb: { width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8, border: '1px solid #eee' },
  removeImgBtn: { position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: '#666', color: '#fff', border: 'none', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  addImgBtn: { width: 72, height: 72, border: '1.5px dashed #b39ddb', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 28, color: '#b39ddb', background: '#f8f5fd' },
  imageCount: { fontSize: 11, color: '#bbb', marginTop: 6 },
  actions: { display: 'flex', gap: 8, justifyContent: 'flex-end' },
  cancelBtn: { padding: '10px 20px', background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, fontSize: 14, color: '#666', cursor: 'pointer' },
  submitBtn: { padding: '10px 24px', background: '#b39ddb', color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
};

export default ReviewWriteModal;
